import { ExclamationCircleIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { useEffect, useRef } from "react";
import { lockScroll, unlockScroll } from "@/utils/scrollLock";

export interface ModalErrorProps {
  isvisible: boolean;
  onClose: () => void;
  error?: string;
  title?: string;
  message?: string | React.ReactNode;
  setIsSelectDisabled?: (disabled: boolean) => void;
  handleKeyPress?: () => void;
  handleDateRef?: () => void;
  setAccept?: (accept: boolean) => void;
  errorList?: [];
  setColorButton?: (color: boolean | string) => void;
  indexSelect?: number | null;
  selectRefs?: React.MutableRefObject<any[] | null>;
  focusSelectInRow?: (index: number | null, refs: any) => void;
  isEditTable?: boolean;
  sizeSmall?: boolean;
  sizeAuto?: boolean;
  buttonLogout?: boolean;
}

const ModalError = ({
  isvisible,
  onClose,
  error = "",
  title = "",
  message = "",
  setIsSelectDisabled = undefined,
  handleKeyPress = () => {},
  handleDateRef = () => {},
  setAccept = () => {},
  errorList = [],
  setColorButton = () => {},
  indexSelect = null,
  selectRefs = undefined,
  focusSelectInRow = () => {},
  isEditTable = false,
  sizeSmall = false,
  sizeAuto = false,
  buttonLogout = false,
}: ModalErrorProps) => {
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isvisible) {
      lockScroll();
      if (modalRef.current) modalRef.current.focus();
      return () => unlockScroll();
    }
  }, [isvisible]);

  if (!isvisible) {
    return null;
  }

  if (isvisible && setIsSelectDisabled) {
    setIsSelectDisabled(false);
  }

  const handleKeyCloseModal = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Enter" || event.key === "Escape") {
      onClose();
      handleKeyPress();
      setAccept(true);
      if (isEditTable && selectRefs?.current) {
        if (indexSelect !== null && selectRefs.current[indexSelect]) {
          selectRefs.current[indexSelect].focus();
        }
      } else {
        focusSelectInRow(indexSelect, selectRefs);
      }
    }
  };

  const handleAccept = () => {
    onClose();
    handleKeyPress();
    setAccept(true);
  };

  const handleClose = () => {
    if (errorList) {
      handleDateRef();
      onClose();
      setAccept(false);
    } else {
      onClose();
      setColorButton(false);
      if (isEditTable && selectRefs?.current) {
        if (indexSelect !== null && selectRefs.current[indexSelect]) {
          selectRefs.current[indexSelect].focus();
        }
      } else {
        focusSelectInRow(indexSelect, selectRefs);
      }
    }
  };
  
  return (
    <div
      ref={modalRef}
      tabIndex={0}
      onKeyDown={handleKeyCloseModal}
      className='fixed inset-0 flex flex-col items-center justify-center bg-black bg-opacity-25 backdrop-blur-sm z-[500]'
    >
      <div
        className={`bg-white p-8 rounded-2xl ${
          sizeSmall ? "w-[280px] " : sizeAuto ? "w-auto mx-2" : "w-[600px]"
        }   flex flex-col items-center `}
      >
        <button
          type='button'
          className='text-dark-blue place-self-end '
          onClick={() => onClose()}
        >
          <XMarkIcon className='w-6 h-6 text-gray-500' />
        </button>
        <ExclamationCircleIcon className='w-12 h-12 mb-2 text-danger' />
        <h1 className='mb-2 text-2xl font-medium text-center text-dark-blue'>
          {title}
        </h1>
        <p className='text-lg text-center text-dark-blue'>{error}</p>
        <div className='w-full text-lg text-center break-words text-dark-blue'>
          {typeof message === "string"
            ? message.split(/\n|<br\s*\/?>/).map((line, idx, arr) => (
                <div
                  key={idx}
                  style={{
                    marginBottom: idx !== arr.length - 1 ? "1px" : "0",
                  }}
                >
                  {line}
                </div>
              ))
            : message}
        </div>
        <div>
          <button
            type='button'
            onClick={handleClose}
            className='px-4 py-3 mt-5 mr-3 font-medium text-white rounded-lg bg-primary-blue hover:bg-second-blue'
          >
            {buttonLogout ? "Login" : errorList ? "Cancel" : "Close"}
          </button>
          {errorList && (
            <button
              onClick={handleAccept}
              className='px-4 py-3 mt-5 mr-3 font-medium text-white rounded-lg bg-primary-blue hover:bg-second-blue'
            >
              Accept
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ModalError;
